import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IPais } from './interfaces/pais.interface';
import { PaisService } from './services/pais.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import { MonedaService } from './services/moneda.service';
import { IMoneda } from './interfaces/moneda.interface';

@Component({
  selector: 'app-pais-form',
  templateUrl: './pais-form.component.html',
  providers: [MessageService]
})
export class PaisFormComponent implements OnInit, OnChanges {
  @Input() model: IPais | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;
  monedas: IMoneda[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private paisService: PaisService,
    private monedaService: MonedaService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadMonedas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadPais();
      } else {
        this.initForm();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      clave_Pais: [{ value: '', disabled: !!this.model }, [Validators.required]],
      descripcion: ['', [Validators.required]],
      moneda: [{ value: '', disabled: !!this.model }, [Validators.required]]
    });
  }

  loadMonedas(): void {
    this.monedaService.getMonedas().subscribe({
      next: (response) => {
        if (response.success) {
          this.monedas = response.data;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las monedas'
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar monedas:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las monedas'
        });
      }
    });
  }

  loadPais(): void {
    if (this.model?.clave_Pais) {
      this.loading = true;
      this.paisService.getPaisDetail(this.model.clave_Pais).subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const pais = response.data[0];
            this.form.patchValue({
              clave_Pais: pais.clave_Pais,
              descripcion: pais.descripcion,
              moneda: pais.moneda
            });
            this.form.get('clave_Pais')?.disable();
            this.form.get('moneda')?.disable();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar el país'
            });
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar país:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar el país'
          });
          this.loading = false;
        }
      });
    }
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    this.paisService.savePais(formData).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({ success: true, message: 'País creado exitosamente' });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.data.error || 'No se pudo crear el país'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al crear país:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear el país'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    this.paisService.updatePais(formData).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({ success: true, message: 'País actualizado exitosamente' });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.data?.error || 'No se pudo actualizar el país'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al actualizar país:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el país'
        });
        this.loading = false;
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'Pais',
        sClave: `${this.form.get('clave_Pais')?.value}`,
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  onDialogClosed(): void {
    this.form.reset();
    this.model = null;
  }
} 